import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { getCookie, setCookie } from '@/lib/cookies'
import { logger } from '@/lib/logger'
import { showErrorData } from '@/lib/toast'
import { useAuthStore } from '@/features/auth/store/auth-store'
import { TAG_GROUPS } from '../types/tags'
import { type Engine } from '../types/type'

export const TAGS_COOKIE_NAME = 'origin_point_discover_tags'

interface DiscoverState {
  // --- 切片 1：搜索参数 (高聚合) ---
  searchParams: {
    searchQuery: string
    executedQuery: string
    page: number
    limit: number
    category?: string
    sort?: string
    order: 'asc' | 'desc'
    tags: string[]
    setSearchQuery: (query: string) => void
    setExecutedQuery: (query: string) => void
    setPage: (page: number | ((prev: number) => number)) => void
    setCategory: (category?: string) => void
    setSort: (sort?: string) => void
    setOrder: (order: 'asc' | 'desc') => void
    setTags: (tags: string[]) => void
  }

  // --- 切片 2：详情控制 ---
  detail: {
    selectedEngine: Engine | null
    isDetailOpen: boolean
    setSelectedEngine: (engine: Engine | null) => void
    setIsDetailOpen: (isOpen: boolean) => void
  }

  // --- 切片 3：标签选择器控制 ---
  tagsSelector: {
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
  }

  // --- 业务编排：Handlers ---
  handlers: {
    handleExecuteSearch: (query: string) => void
    handleSelectEngine: (engine: Engine) => void
    handleCloseDetail: () => void
    handleSetPage: (page: number | ((prev: number) => number)) => void
    handleSetCategory: (category?: string) => void
    handleSetSort: (sort?: string) => void
    handleSetTags: (tags: string[]) => void
    handleToggleTag: (tag: string) => void
    handleExclusiveSelectTag: (
      groupKey: 'identity' | 'orientation' | 'position',
      value: string
    ) => void
    handleClearTags: () => void
    handleSetTagsSelectorOpen: (isOpen: boolean) => void
    handleToggleOrder: () => void
  }
}

const initTags = (() => {
  const saved = getCookie(TAGS_COOKIE_NAME)
  if (saved) {
    try {
      const parsed = JSON.parse(decodeURIComponent(saved))
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      logger.error('Failed to init tags from cookie', e)
    }
  }
  logger.debug('无tags相关cookie')
  return []
})()

export const useDiscoverStore = create<DiscoverState>()(
  devtools(
    (set, get) => ({
      // --- Search Params Slice ---
      searchParams: {
        searchQuery: '',
        executedQuery: '',
        page: 1,
        limit: 20,
        category: undefined,
        sort: 'updated_at',
        order: 'desc' as const,
        tags: initTags,
        setSearchQuery: (query) =>
          set((s) => ({
            searchParams: { ...s.searchParams, searchQuery: query },
          })),
        setExecutedQuery: (query) =>
          set((s) => ({
            searchParams: { ...s.searchParams, executedQuery: query },
          })),
        setPage: (pageUpdate) => {
          const { page: currentPage } = get().searchParams
          const nextPage =
            typeof pageUpdate === 'function'
              ? pageUpdate(currentPage)
              : pageUpdate
          set((s) => ({ searchParams: { ...s.searchParams, page: nextPage } }))
        },
        setCategory: (category) =>
          set((s) => ({ searchParams: { ...s.searchParams, category } })),
        setSort: (sort) =>
          set((s) => ({ searchParams: { ...s.searchParams, sort } })),
        setOrder: (order) =>
          set((s) => ({ searchParams: { ...s.searchParams, order } })),
        setTags: (tags) =>
          set((s) => ({ searchParams: { ...s.searchParams, tags } })),
      },

      // --- Detail Slice ---
      detail: {
        selectedEngine: null,
        isDetailOpen: false,
        setSelectedEngine: (engine) =>
          set((s) => ({ detail: { ...s.detail, selectedEngine: engine } })),
        setIsDetailOpen: (isOpen) =>
          set((s) => ({ detail: { ...s.detail, isDetailOpen: isOpen } })),
      },

      // --- Tags Selector Slice ---
      tagsSelector: {
        isOpen: false,
        setIsOpen: (isOpen) =>
          set((s) => ({ tagsSelector: { ...s.tagsSelector, isOpen } })),
      },

      // --- Handlers ---
      handlers: {
        handleExecuteSearch: (query) => {
          const { user } = useAuthStore.getState().auth
          const { setOpen } = useAuthStore.getState().authDialog

          if (!user) {
            setOpen(true)
            showErrorData('请先登录')
            return
          }

          const { setSearchQuery, setExecutedQuery, setPage } =
            get().searchParams
          setSearchQuery(query)
          setExecutedQuery(query)
          setPage(1)
        },

        handleSelectEngine: (engine) => {
          const { setSelectedEngine, setIsDetailOpen } = get().detail
          setSelectedEngine(engine)
          setIsDetailOpen(true)
        },

        handleCloseDetail: () => {
          get().detail.setIsDetailOpen(false)
        },

        handleSetPage: (pageUpdate) => {
          get().searchParams.setPage(pageUpdate)
        },

        handleSetCategory: (category) => {
          const { setCategory, setPage } = get().searchParams
          setCategory(category)
          setPage(1)
        },

        handleSetSort: (sort) => {
          const { setSort, setPage } = get().searchParams
          setSort(sort)
          setPage(1)
        },

        handleSetTags: (tags) => {
          const { setTags, setPage } = get().searchParams
          setTags(tags)
          setPage(1)
          setCookie(TAGS_COOKIE_NAME, encodeURIComponent(JSON.stringify(tags)))
        },

        handleToggleTag: (tag) => {
          const { tags } = get().searchParams
          const { handleSetTags } = get().handlers
          const nextTags = tags.includes(tag)
            ? tags.filter((t) => t !== tag)
            : [...tags, tag]
          handleSetTags(nextTags)
        },

        handleExclusiveSelectTag: (groupKey, value) => {
          const { tags } = get().searchParams
          const { handleSetTags } = get().handlers
          const group = TAG_GROUPS[groupKey]
          const otherTags = tags.filter(
            (t) => !(group.tags as string[]).includes(t)
          )

          const nextTags = value === 'all' ? otherTags : [...otherTags, value]
          handleSetTags(nextTags)
        },

        handleClearTags: () => {
          get().handlers.handleSetTags([])
        },

        handleSetTagsSelectorOpen: (isOpen) => {
          get().tagsSelector.setIsOpen(isOpen)
        },

        handleToggleOrder: () => {
          const { sort, setSort, setOrder, setPage } = get().searchParams

          if (sort === 'updated_at') {
            // Currently Time Sort -> Switch to Heat Sort (Default)
            setSort(undefined)
            setOrder('desc')
          } else {
            // Currently Heat Sort -> Switch to Time Sort
            setSort('updated_at')
          }
          setPage(1)
        },
      },
    }),
    { name: 'DiscoverStore' }
  )
)
